import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";

// Props type
export type AboutSectionProps = {
  data: any;
  sectionData: any;
  accountName: string;
  organizationName?: string;
  audioContent?: string;
  onToggleAudio?: () => void;
};

// --- Helper Functions

function getScoreColors(score: number) {
  return businessHealthScorePalette(score);
}
function getHealthText(flag: string) {
  switch (flag) {
    case "G":
      return "GREEN";
    case "Y":
      return "YELLOW";
    case "R":
      return "RED";
    default:
      return "N/A";
  }
}
function getRelevanceText(flag: string) {
  switch (flag) {
    case "H":
      return "HIGH";
    case "M":
      return "MEDIUM";
    case "L":
      return "LOW";
    default:
      return "N/A";
  }
}
function isValidUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false;
  try {
    new URL(url.startsWith("http") ? url : `https://${url}`);
    return true;
  } catch {
    return false;
  }
}
function cleanUrl(url: string): string {
  if (!url) return "";
  return url.startsWith("http") ? url : `https://${url}`;
}

function formatLocationValue(location: unknown): string {
  if (!location) return "";
  if (typeof location === "string" || typeof location === "number") {
    return String(location);
  }
  if (typeof location === "object") {
    const loc = location as Record<string, unknown>;
    const parts = [
      loc.streetAddress,
      loc.city,
      loc.state_province,
      loc.zip_pin,
      loc.country,
      loc.region,
      loc.continent,
    ]
      .filter(Boolean)
      .map(value => String(value).trim())
      .filter(Boolean);

    return parts.join(", ");
  }
  return "";
}

// --- Subcomponents

const ScoreBubble: React.FC<{ score: number }> = ({ score }) => {
  const colors = getScoreColors(score);
  return (
    <div style={{ position: "relative", width: 48, height: 48, flexShrink: 0 }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 6,
          left: 6,
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>
          {Math.round(score)}
        </span>
      </div>
    </div>
  );
};

const HealthBadge: React.FC<{ flag: string }> = ({ flag }) => (
  <span
    style={{
      background:
        flag === "G" ? "#16a34a" : flag === "Y" ? "#d97706" : "#dc2626",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 500,
      color: "white",
      padding: "4px 12px",
      marginRight: 8,
      display: "inline-flex",
      gap: 6,
      alignItems: "center",
    }}
  >
    Account Health: <strong>{getHealthText(flag)}</strong>
  </span>
);

const RelevanceBadge: React.FC<{ flag: string; organizationName: string }> = ({
  flag,
  organizationName,
}) => (
  <span
    style={{
      background: "#8b5cf6",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 500,
      color: "white",
      padding: "4px 12px",
      display: "inline-flex",
      gap: 6,
      alignItems: "center",
    }}
  >
    Relevance to {organizationName || "Organization"}:{" "}
    <strong>{getRelevanceText(flag)}</strong>
  </span>
);

const AboutModalHeader: React.FC<{
  accountName: string;
  companyName: string;
  sectionData: any;
  onToggleAudio?: () => void;
}> = ({ accountName, companyName, sectionData, onToggleAudio }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}
  >
    <ScoreBubble score={sectionData?.sectionScore || 0} />
    <div>
      <h3 style={{ margin: 0, fontSize: 18, color: "#8b5cf6" }}>
        <strong>{companyName || accountName}</strong> -{" "}
        <em>About Information</em>
      </h3>
    </div>
    <button
      id="about-audio-btn"
      className="audio-btn"
      title="Play about audio narration"
      onClick={onToggleAudio}
      style={{
        background: "none",
        border: "none",
        padding: 8,
        borderRadius: 6,
        cursor: "pointer",
        transition: "background-color 0.2s",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        width={24}
        height={24}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#8b5cf6"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M11 5 6 9H2v6h4l5 4V5ZM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14" />
      </svg>
    </button>
  </div>
);

const CompanyOverview: React.FC<{
  synopsis?: string;
  mission?: string;
  description?: string;
}> = ({ synopsis, mission, description }) =>
  synopsis || mission || description ? (
    <div style={{ padding: 16 }}>
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span role="img" aria-label="overview">
          🏢
        </span>{" "}
        Company Overview
      </h4>
      {synopsis && (
        <div style={{ marginBottom: 12 }}>
          <h5
            style={{
              margin: "0 0 6px 0",
              fontSize: 14,
              fontWeight: 500,
              color: "#64748b",
            }}
          >
            Synopsis
          </h5>
          <p
            style={{
              margin: 0,
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
        </div>
      )}
      {mission && mission !== synopsis && (
        <div style={{ marginBottom: 12 }}>
          <h5
            style={{
              margin: "0 0 6px 0",
              fontSize: 14,
              fontWeight: 500,
              color: "#64748b",
            }}
          >
            Mission
          </h5>
          <p
            style={{
              margin: 0,
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {mission}
          </p>
        </div>
      )}
      {description && description !== synopsis && description !== mission && (
        <div style={{ marginBottom: 12 }}>
          <h5
            style={{
              margin: "0 0 6px 0",
              fontSize: 14,
              fontWeight: 500,
              color: "#64748b",
            }}
          >
            Description
          </h5>
          <p
            style={{
              margin: 0,
              lineHeight: 1.6,
              fontSize: 14,
              color: "#374151",
            }}
          >
            {description}
          </p>
        </div>
      )}
    </div>
  ) : null;

const CompanyDetails: React.FC<{
  industry?: string;
  location?: string;
  founded?: string;
  employees?: string | number;
  totalCapital?: string | number;
  lastFunding?: string;
}> = ({ industry, location, founded, employees, totalCapital, lastFunding }) =>
  industry ||
  location ||
  founded ||
  employees ||
  totalCapital ||
  lastFunding ? (
    <div style={{ padding: 16, borderTop: "1px solid #f1f5f9" }}>
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span role="img" aria-label="details">
          📊
        </span>{" "}
        Company Details
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        {industry && <DetailsRow label="Industry" value={industry} />}
        {location && <DetailsRow label="Location" value={location} />}
        {founded && <DetailsRow label="Founded" value={founded} />}
        {employees && <DetailsRow label="Employees" value={employees} />}
        {totalCapital && (
          <DetailsRow label="Total Capital" value={totalCapital} />
        )}
        {lastFunding && <DetailsRow label="Last Funding" value={lastFunding} />}
      </div>
    </div>
  ) : null;

const DetailsRow: React.FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontSize: 13,
      padding: "8px 12px",
      background: "#f8fafc",
      borderRadius: 6,
    }}
  >
    <span style={{ color: "#64748b", fontWeight: 500 }}>{label}:</span>
    <span
      style={{
        color: "#1e293b",
        fontWeight: 600,
        textAlign: "right",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: 150,
      }}
    >
      {value}
    </span>
  </div>
);

const OfficialLinks: React.FC<{
  website?: string;
  linkedinUrl?: string;
  crunchbaseUrl?: string;
}> = ({ website, linkedinUrl, crunchbaseUrl }) => {
  const hasAny =
    (website && isValidUrl(website)) ||
    (linkedinUrl && isValidUrl(linkedinUrl)) ||
    (crunchbaseUrl && isValidUrl(crunchbaseUrl));
  if (!hasAny) return null;
  return (
    <div style={{ padding: 16, borderTop: "1px solid #f1f5f9" }}>
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span role="img" aria-label="links">
          🔗
        </span>{" "}
        Official Links
      </h4>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {website && isValidUrl(website) && (
          <a
            href={cleanUrl(website)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 12px",
              background: "#3b82f6",
              color: "white",
              textDecoration: "none",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            🌐 Website
          </a>
        )}
        {linkedinUrl && isValidUrl(linkedinUrl) && (
          <a
            href={cleanUrl(linkedinUrl)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 12px",
              background: "#0a66c2",
              color: "white",
              textDecoration: "none",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            💼 LinkedIn
          </a>
        )}
        {crunchbaseUrl && isValidUrl(crunchbaseUrl) && (
          <a
            href={cleanUrl(crunchbaseUrl)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 12px",
              background: "#0d7377",
              color: "white",
              textDecoration: "none",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            🚀 Crunchbase
          </a>
        )}
      </div>
    </div>
  );
};

const KeyMarkets: React.FC<{
  geoMarkets?: any[];
  productMarkets?: any[];
}> = ({ geoMarkets = [], productMarkets = [] }) => {
  if (
    (!geoMarkets || geoMarkets.length === 0) &&
    (!productMarkets || productMarkets.length === 0)
  )
    return null;
  return (
    <div style={{ padding: 16, borderTop: "1px solid #f1f5f9" }}>
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: 16,
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span role="img" aria-label="markets">
          🌍
        </span>{" "}
        Key Markets
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 16,
        }}
      >
        {!!geoMarkets?.length && (
          <div>
            <h5
              style={{
                margin: "0 0 8px 0",
                fontSize: 14,
                fontWeight: 500,
                color: "#64748b",
              }}
            >
              Geographic Markets
            </h5>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {geoMarkets.filter(Boolean).map((market, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-block",
                    padding: "4px 8px",
                    background: "#e0e7ff",
                    color: "#3730a3",
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {market}
                </span>
              ))}
            </div>
          </div>
        )}
        {!!productMarkets?.length && (
          <div>
            <h5
              style={{
                margin: "0 0 8px 0",
                fontSize: 14,
                fontWeight: 500,
                color: "#64748b",
              }}
            >
              Product Markets
            </h5>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {productMarkets.filter(Boolean).map((market, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-block",
                    padding: "4px 8px",
                    background: "#f3e8ff",
                    color: "#6b21a8",
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {market}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AboutModalBody: React.FC<{
  synopsis?: string;
  mission?: string;
  description?: string;
  industry?: string;
  location?: string;
  founded?: string;
  employees?: string | number;
  website?: string;
  linkedinUrl?: string;
  crunchbaseUrl?: string;
  geoMarkets?: any[];
  productMarkets?: any[];
  totalCapital?: string | number;
  lastFunding?: string;
  healthFlag: string;
  relevanceFlag: string;
  organizationName: string;
}> = props => (
  <div
    style={{
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      marginBottom: 16,
    }}
  >
    {/* Badges */}
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "12px 16px",
        background: "#f8fafc",
        borderBottom: "1px solid #e5e7eb",
      }}
    >
      <HealthBadge flag={props.healthFlag} />
      <RelevanceBadge
        flag={props.relevanceFlag}
        organizationName={props.organizationName}
      />
    </div>
    <CompanyOverview
      synopsis={props.synopsis}
      mission={props.mission}
      description={props.description}
    />
    <CompanyDetails
      industry={props.industry}
      location={props?.location}
      founded={props.founded}
      employees={props.employees}
      totalCapital={props.totalCapital}
      lastFunding={props.lastFunding}
    />
    <OfficialLinks
      website={props.website}
      linkedinUrl={props.linkedinUrl}
      crunchbaseUrl={props.crunchbaseUrl}
    />
    <KeyMarkets
      geoMarkets={props.geoMarkets}
      productMarkets={props.productMarkets}
    />
  </div>
);

// --- MAIN EXPORT

export const AboutSection: React.FC<AboutSectionProps> = ({
  data,
  sectionData,
  accountName,
  organizationName,
  audioContent,
  onToggleAudio,
}) => {
  if (!data) return <div>No about data available</div>;
  // Fallbacks for undefined
  const name = data?.name || "";
  const healthFlag = sectionData?.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData?.sectionRelevanceFlag || "N/A";
  const formattedLocation = formatLocationValue(
    data?.location || data?.headquarters
  );
  return (
    <div
      className="about-section-consolidated"
      data-modal-header={accountName}
      data-audio-content={audioContent || ""}
    >
      <AboutModalHeader
        accountName={accountName}
        companyName={name}
        sectionData={sectionData}
        onToggleAudio={onToggleAudio}
      />
      <AboutModalBody
        synopsis={data?.synopsis}
        mission={data?.mission}
        description={data?.description}
        industry={data?.industry}
        location={formattedLocation}
        founded={data?.founded || data?.incorporationDate}
        employees={data?.employees || data?.employeeCount}
        website={data?.website || data?.websiteURL}
        linkedinUrl={data?.linkedinUrl || data?.linkedInURL}
        crunchbaseUrl={data?.crunchbaseUrl || data?.crunchbaseURL}
        geoMarkets={data?.geoMarkets}
        productMarkets={data?.productMarkets}
        totalCapital={data?.totalCapital}
        lastFunding={data?.lastFunding}
        healthFlag={healthFlag}
        relevanceFlag={relevanceFlag}
        organizationName={organizationName || ""}
      />
    </div>
  );
};
